import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { createSupplierEnquiry, updateSupplierEnquiry } from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../../components/ui';

import { moduleConfig } from './supplierEnquiriesModuleConfig';
import { SupplierEnquiryLine, useSupplierEnquiry } from './useData';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';
import { supplyBlockedActionMessage } from '../blockingMessages';

type LineForm = {
  key: string;
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  qty: string;
  requisitionQty: string;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  unitId: '',
  qty: '',
  requisitionQty: '',
  itemNotes: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

function formatItemLabel(
  vendorCode: string | null | undefined,
  name: string | null | undefined,
  unitId: string | number | null | undefined,
  units: { id: number; ucode?: string }[],
  unitUcodeFromItem?: string | null,
): string {
  const v = (vendorCode ?? '').trim() || '';
  const n = (name ?? '').trim() || '';
  let ucode = (unitUcodeFromItem ?? '').trim();
  if (!ucode && unitId != null && unitId !== '') {
    const uid = typeof unitId === 'string' ? Number(unitId) : unitId;
    if (typeof uid === 'number' && !Number.isNaN(uid)) {
      ucode = units.find((x) => x.id === uid)?.ucode?.trim() ?? '';
    }
  }
  return ucode ? `${v} - ${n} - (${ucode})` : `${v} - ${n}`;
}

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(
    moduleConfig.module.route.list,
    getLocationReturnPath(location.state),
  );
  const [searchParams] = useSearchParams();
  const editMatch = matchPath(
    { path: '/supplies/supplier-enquiries/:id/edit', end: true },
    location.pathname,
  );
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const enquiryId =
    rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;

  const { paramRequisitionId, paramCaseId } = React.useMemo(() => {
    const r = searchParams.get('requisitionId');
    const c = searchParams.get('caseId');
    const reqN = r != null ? Number(r) : NaN;
    const caseN = c != null ? Number(c) : NaN;
    return {
      paramRequisitionId: Number.isFinite(reqN) && reqN > 0 ? Math.floor(reqN) : undefined,
      paramCaseId: Number.isFinite(caseN) && caseN > 0 ? Math.floor(caseN) : undefined,
    };
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSupplierEnquiry(enquiryId);
  const { data: units = [] } = useSWR<any[]>('/store-units', fetcher);
  const {
    data: requisitionForNew,
    isLoading: loadingRequisitionForNew,
    error: requisitionForNewError,
  } = useSWR<{
    id: number;
    caseId?: number | null;
  }>(!isEdit && paramRequisitionId ? `/supply-requisitions/${paramRequisitionId}` : null, fetcher);
  const { data: suppliersRes } = useSWR<any>('/suppliers?page=1&limit=500', fetcher);
  const suppliersList = React.useMemo(
    () => (Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data ?? []),
    [suppliersRes],
  );
  const supplierOptions = React.useMemo(() => {
    const rows = (suppliersList as { id: number; name: string; code?: string | null }[]).map((s) => ({
      value: String(s.id),
      label: s.code ? `${s.name} (${s.code})` : s.name,
    }));
    if (
      existing?.supplierId &&
      existing?.supplier &&
      !rows.some((o) => o.value === String(existing.supplierId))
    ) {
      const s = existing.supplier;
      rows.push({
        value: String(s.id),
        label: s.code ? `${s.name} (${s.code})` : s.name,
      });
    }
    return [{ value: '', label: 'Select supplier' }, ...rows];
  }, [suppliersList, existing?.supplierId, existing?.supplier]);

  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    enqDate: '',
    supplierId: '',
    ref: '',
    notes: '',
  });

  const { data: itemsResponse } = useSWR<any>('/store-items?page=1&limit=200', fetcher);
  const items = React.useMemo<any[]>(
    () => (Array.isArray(itemsResponse) ? itemsResponse : itemsResponse?.data ?? []),
    [itemsResponse],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['enquiry-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/store-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);

  React.useEffect(() => {
    if (!existing) return;
    setFormData({
      enqDate: existing.enqDate ? existing.enqDate.slice(0, 10) : '',
      supplierId: String(existing.supplierId),
      ref: existing.ref || '',
      notes: existing.notes || '',
    });
    const mapped = (existing.lines || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty !== null && line.qty !== undefined ? String(line.qty) : '',
      requisitionQty:
        line.requisitionQty !== null && line.requisitionQty !== undefined ? String(line.requisitionQty) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [existing]);

  React.useEffect(() => {
    if (!itemsById.size) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.itemId || line.level1Filter?.trim()) return line;
        const item = itemsById.get(line.itemId);
        if (!item) return line;
        changed = true;
        return { ...line, level1Filter: (item.level1 ?? '').trim() };
      });
      return changed ? next : prev;
    });
  }, [itemsById, enquiryId]);

  const setLine = (key: string, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescr = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };

  const onItemChange = (lineKey: string, itemId: string) => {
    if (!itemId) {
      setLine(lineKey, { itemId: '', itemDescr: '', itemVcode: '', unitId: '' });
      return;
    }
    const item = itemsById.get(itemId);
    setLine(lineKey, {
      itemId,
      itemDescr: item?.name || '',
      itemVcode: item?.vendorCode || '',
      unitId: item?.unitId ? String(item.unitId) : '',
      level1Filter: (item?.level1 ?? '').trim(),
    });
  };

  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const options = [
        { value: '', label: 'Select item' },
        ...filtered.map((item: any) => ({
          value: String(item.id),
          label: formatItemLabel(item.vendorCode, item.name, item.unitId, units, item.unit?.ucode),
        })),
      ];
      if (line.itemId && !options.some((o) => o.value === line.itemId)) {
        options.push({
          value: line.itemId,
          label: formatItemLabel(line.itemVcode, line.itemDescr, line.unitId, units),
        });
      }
      return options;
    },
    [items, levelItemsMap, units],
  );

  const unitOptions = React.useMemo(
    () => [
      { value: '', label: 'Unit' },
      ...units.map((unit) => ({ value: String(unit.id), label: unit.descr || unit.ucode || `Unit #${unit.id}` })),
    ],
    [units],
  );

  const normalizedLines: SupplierEnquiryLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        qty: asNumber(line.qty),
        requisitionQty: asNumber(line.requisitionQty),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter((line) => line.itemId || line.itemDescr || line.qty || line.itemNotes);
  }, [lines]);

  const submit = async () => {
    if (isEdit && (existing?._count?.quotations ?? 0) > 0) return;
    const supplierId = asNumber(formData.supplierId);
    if (!supplierId) return;

    let caseId: number;
    let requisitionId: number | null | undefined;
    if (isEdit) {
      if (existing == null || existing.caseId == null) return;
      caseId = existing.caseId;
      requisitionId = existing.requisitionId != null ? existing.requisitionId : null;
    } else if (paramRequisitionId) {
      if (!requisitionForNew?.caseId) return;
      caseId = requisitionForNew.caseId;
      requisitionId = requisitionForNew.id;
    } else if (paramCaseId) {
      caseId = paramCaseId;
      requisitionId = null;
    } else {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        enqDate: formData.enqDate || undefined,
        caseId,
        supplierId,
        ref: formData.ref || undefined,
        requisitionId: requisitionId === undefined ? undefined : requisitionId,
        notes: formData.notes || undefined,
        lines: normalizedLines,
      };
      if (isEdit && enquiryId) {
        await updateSupplierEnquiry(enquiryId, payload);
      } else {
        await createSupplierEnquiry(payload);
      }
      goBack();
    } finally {
      setIsSaving(false);
    }
  };

  const supplierIdNum = asNumber(formData.supplierId);
  const newContextOk =
    !isEdit &&
    (() => {
      if (paramRequisitionId) {
        if (loadingRequisitionForNew) return false;
        if (requisitionForNewError) return false;
        return requisitionForNew != null && requisitionForNew.caseId != null;
      }
      if (paramCaseId) return true;
      return false;
    })();
  const requisitionMissingCase =
    !isEdit &&
    paramRequisitionId &&
    !loadingRequisitionForNew &&
    !requisitionForNewError &&
    requisitionForNew != null &&
    requisitionForNew.caseId == null;
  const canSave = Boolean(
    supplierIdNum &&
      (isEdit
        ? existing != null && existing.caseId != null
        : newContextOk),
  );
  const enquiryCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = existing?.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.supplyCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, existing?.caseId, existing?.supplyCase]);
  const shouldLockRequisitionLineFields = React.useCallback(
    (line: LineForm) => {
      if (!isEdit) return false;
      if (!existing?.caseId) return false;
      // Requisition-origin lines carry a requisition quantity in this form.
      return line.requisitionQty !== '';
    },
    [isEdit, existing?.caseId],
  );
  const editBlockedByQuotation = isEdit && (existing?._count?.quotations ?? 0) > 0;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Card
        variant="outlined"
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
          'border shadow-sm',
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <Button type="submit" disabled={isSaving || loadingExisting || !canSave || editBlockedByQuotation} loading={isSaving} size="default">
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Card>

      {enquiryCaseCaption ? (
        <Callout variant="success" title="Linked supply case">{enquiryCaseCaption}</Callout>
      ) : null}
      {editBlockedByQuotation ? (
        <Callout variant="danger" title="Cannot edit RFQ">{supplyBlockedActionMessage('ENQ_EDIT_LINKED_QUOTATION')}</Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="RFQ details"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            {requisitionForNewError ? (
              <p className={cn('mb-4 text-sm text-red-600')}>Could not load the requisition for this link.</p>
            ) : null}
            {requisitionMissingCase ? (
              <p className={cn('mb-4 text-sm text-amber-700 dark:text-amber-300')}>
                This requisition is not linked to a supply case. Link it to a case on the requisition first.
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-2">
                <Label htmlFor="enq-date">Enquiry date</Label>
                <DateInput
                  id="enq-date"
                  value={formData.enqDate}
                  onChange={(e) => setFormData((s) => ({ ...s, enqDate: e.target.value }))}
                  placeholder="Select date"
                  showFormatHint={false}
                  disabled={editBlockedByQuotation}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="enq-supplier">Supplier</Label>
                <Select
                  id="enq-supplier"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={(e) => setFormData((s) => ({ ...s, supplierId: e.target.value }))}
                  options={supplierOptions}
                  placeholder="Select supplier"
                  showClearButton={false}
                  disabled={editBlockedByQuotation}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="enq-ref">Reference</Label>
                <TextInput
                  id="enq-ref"
                  value={formData.ref}
                  onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))}
                  placeholder="Reference"
                  showClearButton
                  disabled={editBlockedByQuotation}
                />
              </div>
              <div className="md:col-span-5">
                <Label htmlFor="enq-notes">Notes</Label>
                <Textarea
                  id="enq-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
                  rows={2}
                  showClearButton={false}
                  className="min-h-[72px]"
                  disabled={editBlockedByQuotation}
                />
              </div>
            </div>
          </AccordionItem>
          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add line"
                aria-label="Add line"
                onClick={() => setLines((prev) => [...prev, newLine()])}
                disabled={editBlockedByQuotation}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b', colors.border)}>
                <th className="w-[10rem] min-w-[10rem] max-w-[10rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                <th className="px-2 py-2 text-left text-xs font-medium">Item</th>
                <th className="w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] px-2 py-2 text-left text-xs font-medium">Unit</th>
                <th className="w-24 min-w-[6rem] max-w-[6rem] px-2 py-2 text-left text-xs font-medium">Req. qty</th>
                <th className="w-24 min-w-[6rem] max-w-[6rem] px-2 py-2 text-left text-xs font-medium">Qty</th>
                <th className="w-[12rem] min-w-[12rem] max-w-[12rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                <th className="px-2 py-2 text-right text-xs font-medium"> </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const lockRequisitionFields = shouldLockRequisitionLineFields(line);
                return (
                <tr key={line.key} className="align-top">
                  <td className="w-[10rem] min-w-[10rem] max-w-[10rem] p-2">
                    <Select
                      value={line.level1Filter}
                      onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                      options={level1Options}
                      placeholder="Level 1"
                      showClearButton={false}
                      className="w-full text-sm"
                      disabled={lockRequisitionFields || editBlockedByQuotation}
                    />
                  </td>
                  <td className="p-2">
                    <Select
                      value={line.itemId}
                      onChange={(e) => onItemChange(line.key, e.target.value)}
                      options={getItemOptionsForLine(line)}
                      placeholder="Select item"
                      showClearButton={false}
                      className="w-full text-sm"
                      disabled={lockRequisitionFields || editBlockedByQuotation}
                    />
                  </td>
                  <td className="w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] p-2">
                    <Select
                      value={line.unitId}
                      onChange={(e) => setLine(line.key, { unitId: e.target.value })}
                      options={unitOptions}
                      placeholder="Unit"
                      showClearButton={false}
                      disabled={lockRequisitionFields || editBlockedByQuotation}
                    />
                  </td>
                  <td className="p-2 w-24 align-middle">
                    <NumberInput
                      size="sm"
                      value={line.requisitionQty}
                      readOnly
                      showClearButton={false}
                      placeholder="Req."
                      className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                    />
                  </td>
                  <td className="p-2 w-24 align-middle">
                    <NumberInput
                      size="sm"
                      value={line.qty}
                      onChange={(e) =>
                        setLine(line.key, {
                          qty: e.target.value === null || e.target.value === undefined ? '' : String(e.target.value),
                        })
                      }
                      showClearButton={false}
                      placeholder="Qty"
                      className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                      readOnly={editBlockedByQuotation}
                    />
                  </td>
                  <td className="w-[12rem] min-w-[12rem] max-w-[12rem] p-2">
                    <TextInput
                      size="sm"
                      value={line.itemNotes}
                      onChange={(e) => setLine(line.key, { itemNotes: e.target.value })}
                      showClearButton={false}
                      placeholder="Notes"
                      disabled={editBlockedByQuotation}
                    />
                  </td>
                  <td className="p-2 text-right align-middle">
                    <Button
                      type="button"
                      variant="danger"
                      size="icon"
                      title={lines.length > 1 ? 'Remove line' : 'Clear line'}
                      aria-label={lines.length > 1 ? 'Remove line' : 'Clear line'}
                      onClick={() =>
                        setLines((prev) => {
                          if (prev.length > 1) {
                            return prev.filter((r) => r.key !== line.key);
                          }
                          return [newLine()];
                        })
                      }
                      disabled={editBlockedByQuotation}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </form>
  );
};

export default Form;
